import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const LINKS = [
  { label: "Home", to: "/" },
  { label: "How It Works", to: "/#how-it-works", hash: true },
  { label: "About", to: "/about" },
  { label: "Support", to: "/support" },
];

const linkClass = (active) =>
  `text-sm font-medium transition-colors duration-200 hover:text-blue-600 ${
    active ? "text-blue-600" : "text-slate-600"
  }`;

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header
      data-testid="site-nav"
      className="fixed inset-x-0 top-0 z-50 border-b border-slate-200/80 bg-white/90 backdrop-blur-md"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center" onClick={() => setOpen(false)}>
          <img
            src="/assets/delsha-wordmark.png"
            alt="DELSHA"
            className="h-7 w-auto sm:h-8"
          />
        </Link>

        <nav className="hidden items-center gap-8 md:flex" aria-label="Primary">
          {LINKS.map((link) =>
            link.hash ? (
              <Link
                key={link.label}
                to={link.to}
                data-testid="nav-link-how-it-works"
                className={linkClass(false)}
              >
                {link.label}
              </Link>
            ) : (
              <NavLink
                key={link.label}
                to={link.to}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className={({ isActive }) => linkClass(isActive)}
              >
                {link.label}
              </NavLink>
            ),
          )}
          <Link
            to="/#download"
            data-testid="nav-get-app-button"
            className="rounded-full bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-blue-700 hover:shadow-md"
          >
            Get the App
          </Link>
        </nav>

        <button
          type="button"
          data-testid="nav-mobile-menu-button"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="flex h-10 w-10 items-center justify-center rounded-lg text-slate-700 transition-colors hover:bg-slate-100 md:hidden"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <nav
          data-testid="nav-mobile-menu"
          className="border-t border-slate-200/80 bg-white px-4 pb-6 pt-3 md:hidden"
          aria-label="Mobile"
        >
          <div className="flex flex-col gap-1">
            {LINKS.map((link) => (
              <Link
                key={link.label}
                to={link.to}
                data-testid={`nav-mobile-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setOpen(false)}
                className={`rounded-lg px-3 py-3 text-base font-medium transition-colors hover:bg-blue-50 hover:text-blue-600 ${
                  location.pathname === link.to ? "text-blue-600" : "text-slate-700"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/#download"
              data-testid="nav-mobile-get-app-button"
              onClick={() => setOpen(false)}
              className="mt-2 rounded-full bg-blue-600 px-5 py-3 text-center text-base font-semibold text-white"
            >
              Get the App
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
};
